import {
  BillCheckoutInput,
  CoBillFragment,
  CoDepositAssignedFragment,
  CoDepositFragment,
  CoDocumentFragment,
  CoProductionAssignedFragment,
  CoReservationFragment,
  DepositCheckoutInput,
  DocumentPaymentDetailInput,
  FiscalType,
  ProductionCheckoutInput,
  ReferenceCustomerFragment,
} from 'src/api/api.generated'
import { CreateCoDocumentInput, UpdateCoDocumentInput, UpdateDocumentPaymentDetailsInput, UpsertCoDocumentInput } from './checkout-document-form.api'
import { CheckoutDocumentFormSchema, DocumentDetailsSchema } from './checkout-document-form.schema'
import * as R from 'ramda'
import { CoData, CoQuery } from './components/co-query-wrapper'
import { DocumentCustomerFormSchema } from '../../../../components/document-customer-input/document-customer-schema'
import { TFunction } from 'i18next'
import { DocumentPaymentDetailsWithUUID } from './checkout-document-form.types'

export function reduceProductionAssigneds(bills: CoBillFragment[]): CoProductionAssignedFragment[] {
  return bills.reduce((prev: CoProductionAssignedFragment[], current) => [...prev, ...(current.productions || [])], [])
}

export function getProductionAssignedTotal(pas: CoProductionAssignedFragment[]): number {
  const total = pas.reduce((prev, current) => {
    const quota = current.production.total * current.quota
    return prev + quota
  }, 0)

  return total
}

export function reduceDepositAssigneds(deposits: CoDepositFragment[]): CoDepositAssignedFragment[] {
  return deposits.reduce((prev: CoDepositAssignedFragment[], current) => [...prev, ...(current.depositAssigneds || [])], [])
}

export function getFiscalSum(das: { fiscalType: FiscalType; usedAmount: number | null }[]): number {
  return (das || []).reduce((prev, current) => {
    return prev + (current.fiscalType !== FiscalType.NON_FISCAL ? current.usedAmount || 0 : 0)
  }, 0)
}

export function getNonFiscalSum(das: { fiscalType: FiscalType; usedAmount: number | null }[]): number {
  return (das || []).reduce((prev, current) => {
    return prev + (current.fiscalType === FiscalType.NON_FISCAL ? current.usedAmount || 0 : 0)
  }, 0)
}

export function getUpsertDocumentInput({
  queryData,
  draft,
  finalized,
  updatePaymensDetailsOnly,
  data,
  t,
  paymentDetailsWithUUID,
  createReceiptZero,
}: {
  queryData: CoQuery
  draft: boolean
  finalized: boolean
  updatePaymensDetailsOnly: boolean
  data: CheckoutDocumentFormSchema
  t: TFunction
  paymentDetailsWithUUID: DocumentPaymentDetailsWithUUID
  createReceiptZero?: boolean
}): UpsertCoDocumentInput | UpdateDocumentPaymentDetailsInput | null {
  if (queryData.mode === 'UPDATE' || queryData.mode === 'READ_ONLY') {
    const document = queryData.document
    const deposits: DocumentPaymentDetailInput[] = document.documentPaymentDetails
      .filter((pd) => pd.referenceDocument?.documentType === 'ADVANCE')
      ?.map((pd) => ({
        paymentActiveId: pd.paymentActiveId,
        amount: Math.round(pd.amount),
        expireDate: pd.expireDate,
        referenceDocumentId: pd.referenceDocument?.id,
      }))
    if (queryData.mode === 'READ_ONLY' && updatePaymensDetailsOnly && paymentDetailsWithUUID) {
      const input: UpdateDocumentPaymentDetailsInput = {
        id: queryData.document.id,
        paymentActivesIds: paymentDetailsWithUUID
          .map((p) => data.payments.find((i) => i.uuid === p.uuid) ?? p)
          .filter(({ paymentActiveId }) => paymentActiveId)
          .map(({ paymentActiveId }) => paymentActiveId) as string[], //TypeScript anomaly
      }
      return input
    } else {
      const input: UpdateCoDocumentInput = {
        documentId: document.id,
        draft,
        finalized,
        cashDeskId: data.cashDeskId,
        customerId: data.customer.id,
        documentType: document.documentType,
        fiscalType: data.fiscalType,
        payments: [
          ...(data.payments
            .filter((p) => p.paymentActiveId)
            .map((p) => {
              const { editable, uuid, ...other } = p
              return {
                ...other,
                amount: Math.round(other.amount),
              }
            }) as DocumentPaymentDetailInput[]),
          ...deposits,
        ],
        documentDetails: data.details,
        scelNote: data.scelNote,
        note: data.fiscalType === 'INVOICE' || data.fiscalType === 'RECEIPT' ? data.note : undefined,
      }
      return input
    }
  } else if (queryData.mode === 'CREATE') {
    const note =
      data.addNoteToZeroReceipt && data.companyToAddZeroReceipt && data.fiscalType === 'RECEIPT'
        ? `${t('pms.common.checkout_document.note_to_zero_receipt')} ${data.companyToAddZeroReceipt}${data.note ? `\n\n${data.note}` : ''}`
        : data.note

    const input: CreateCoDocumentInput = {
      draft,
      finalized: false,
      amount: Math.round(data.amounts?.totalAmount || 0),
      cashDeskId: data.cashDeskId,
      customerId: data.customer.id,
      fiscalType: data.fiscalType,
      payments: data.payments
        .filter((p) => p.paymentActiveId)
        .map((p) => {
          const { editable, uuid, ...other } = p
          return {
            ...other,
            amount: Math.round(other.amount),
          }
        }) as DocumentPaymentDetailInput[],
      billCheckout: getBillCheckoutInput(reduceProductionAssigneds(queryData.bills), data.details),
      deposits: getDepositsInput(data),
      scelNote: data.scelNote,
      note: data.fiscalType === 'INVOICE' || data.fiscalType === 'RECEIPT' ? note : undefined,
      groupBy: data.groupBy ?? 'ANALITIC',
      createReceiptZero,
    }

    return input
  } else {
    return null
  }
}

export function getReferenceCustomers(reservations: CoReservationFragment[], document?: CoDocumentFragment, bills?: CoBillFragment[]): ReferenceCustomerFragment[] {
  let rcs = reservations.reduce((p: ReferenceCustomerFragment[], r) => {
    const rcs: ReferenceCustomerFragment[] = [r.reference.mainGuest]
    if (r.reference.customerPerson && !r.reference.customerPerson.dummyCustomer) {
      rcs.push(r.reference.customerPerson)
    }
    if (r.reference.customerCompany && !r.reference.customerCompany.dummyCustomer) {
      rcs.push(r.reference.customerCompany)
    }
    if (r.reference.agency && !r.reference.agency.dummyCustomer) {
      rcs.push(r.reference.agency)
    }

    return [...p, ...rcs]
  }, [])

  // Se sto pagando qualcosa di una singola prenotazione
  if (reservations.length === 1 && reservations[0].reservedResources) {
    // e non ci sono bill associati a più rr
    const thereAreBillIsAssignedToMultipleRR = bills?.find((b) => b.reservedResourceId && b.reservedResourceId.length > 1) !== undefined
    // e non ci sono bill associati a rr diverse
    const thereAreBillAssignedToDifferentRR = bills?.find((b) => bills[0].reservedResourceId !== b.reservedResourceId) !== undefined

    const rrId = bills?.[0]?.reservedResourceId?.[0]
    if (rrId && bills && bills.length > 0 && !thereAreBillIsAssignedToMultipleRR && !thereAreBillAssignedToDifferentRR) {
      reservations[0].reservedResources
        ?.find((rr) => rr.id === rrId)
        ?.guestOccupancies?.forEach((go) => {
          rcs.push(go.guest)
        })
    }
  }

  if (document?.customer) {
    const isDraft = document.auditable.state === 'DRAFT'
    const customerIsEditable =
      document.fiscalType === 'INVOICE' &&
      document.documentType !== 'ADVANCE' &&
      (document.felAdditionalInfo === null || !document.felAdditionalInfo?.sent || document.felAdditionalInfo.state === 'SCARTATO')
    const consolidatedData = document.consolidatedData?.customer
    if ((isDraft || customerIsEditable) && consolidatedData) {
      // L'anagrafica utilizzata nel documento deve avere precedenza rispetto a quella associata alla prenotazione
      rcs = rcs.filter((c) => c.id !== consolidatedData.id)
      rcs.push(consolidatedData)
    } else {
      rcs = rcs.filter((c) => c.id !== document.customerId)
      rcs.push({ ...document.customer, id: document.customerId })
    }
  }

  const billsOwner = (bills ?? []).reduce((p: ReferenceCustomerFragment[], r) => {
    if (!r.owner.dummyCustomer) return [...p, r.owner]
    return [...p]
  }, [])

  billsOwner.forEach((billOwner) => {
    rcs.unshift(billOwner)
  })

  return R.uniqBy((c) => c.id, rcs)
}

function billOwnerToSchema(bill: CoBillFragment): DocumentCustomerFormSchema {
  const owner = bill.owner
  return {
    fiscalCode: owner.fiscalCode || undefined,
    vatNumber: owner.vatNumber || undefined,
    countryCode: owner.address?.country?.isoCode,
    municipalityId: owner.address?.municipalityId ?? undefined,
    id: owner.id,
    address: owner.address?.address || '',
    name: owner.name || '',
    splitPayment: owner.company?.splitPayment ?? false,
    city: owner.address?.city ?? undefined,
    postalCode: owner.address?.postalCode ?? undefined,
    province: owner.address?.province ?? undefined,
    sdiCode: owner.company?.sdiCode ?? undefined,
    email: owner.mailContacts?.find((x) => x.type === 'MAIN')?.value ?? owner.mailContacts?.length ? owner.mailContacts[0].value : undefined,
  }
}

export function getDocumentDefaultCustomer(queryData: CoData): DocumentCustomerFormSchema {
  if (queryData.mode === 'CREATE') {
    // const referenceCustomers = getReferenceCustomers(queryData.reservations, undefined, queryData.bills)
    // if (referenceCustomers.length > 0) {
    //   const companyAsOwner = referenceCustomers
    //     .filter((dummyCustomers) => !dummyCustomers.dummyCustomer)
    //     .find((customer) => customer.type === CustomerType.COMPANY && customer.company?.defaultCredit !== CreditType.NC)
    //   return customerFragmentToSchema(companyAsOwner ?? referenceCustomers[0])
    // }

    // const billsWithNonDummyOwner = queryData.bills.filter((c) => !c.owner.dummyCustomer)
    // if (billsWithNonDummyOwner.length > 0) {
    //   return billOwnerToSchema(billsWithNonDummyOwner[0])
    // }
    const billCustomers = queryData.bills.filter((bill) => !bill.owner.dummyCustomer).map(billOwnerToSchema)

    const uniqueBillCustomers = R.uniqBy((c) => c.id, billCustomers)
    if (uniqueBillCustomers.length === 1) {
      return uniqueBillCustomers[0]
    }
  }

  return {
    id: '',
    countryCode: '',
    address: '',
    splitPayment: false,
  }
}

export function getBillCheckoutInput(pas: CoProductionAssignedFragment[], documentDetails?: DocumentDetailsSchema): BillCheckoutInput[] {
  const byBillId = R.groupBy((pa: CoProductionAssignedFragment) => pa.bill.id)
  const paByBillId = byBillId(pas)

  const billCheckoutInput: BillCheckoutInput[] = []

  for (const billId in paByBillId) {
    const billPas = paByBillId[billId]
    if (billPas.length > 0) {
      const productions: ProductionCheckoutInput[] = []
      billPas.forEach((pa) => {
        const description = documentDetails?.find((dd) => dd.productionAssignedIs === pa.id)?.description
        const paQuota = pa.production.total * pa.quota
        productions.push({
          productionAssignedId: pa.id,
          amount: Math.round(paQuota),
          description,
        })
      })

      billCheckoutInput.push({
        billId: billId,
        productions: productions,
      })
    }
  }
  return billCheckoutInput
}

function getDepositsInput(data: CheckoutDocumentFormSchema): DepositCheckoutInput[] {
  return (
    data.deposits?.reduce((prev: DepositCheckoutInput[], current) => {
      if (current.depositAssignedId && current.usedAmount !== null && current.usedAmount > 0) {
        return [...prev, { depositAssignedId: current.depositAssignedId, amount: current.usedAmount }]
      }
      return prev
    }, []) ?? []
  )
}
